/**
 *  FormE.jsx — Purchased Precursors for SEE Calculation (Sheet E_PurchPrec)
 *
 *  Revision (Jun 2026) — full UI redesign matching Forms A/B/C/D aesthetic:
 *
 *    1. Slim single-line header — "Purchased Precursors"
 *       (no "Form E", no "Sheet E_PurchPrec — Purchased precursors..." header,
 *        no dark navy "Data input for the determination..." banner)
 *    2. AuditSidebar wrapper, same as A/B/C/D
 *    3. Top "Add precursor" button GONE. Replaced with a small "+" tab at
 *       the end of the precursor tab strip (cleaner, matches tab UX).
 *    4. Both top-of-form Alerts ("X precursors auto-loaded..." / "No
 *       purchased precursors found...") removed. The slim info banner inside
 *       each precursor's sub-tab 01 carries the linked-from-Form-A context.
 *    5. Precursor tabs at the top — one precursor visible at a time. Each
 *       tab shows title + country chip + % completeness badge.
 *    6. Within each precursor: 2 sub-tabs (matching Form D pattern):
 *         - Sub-tab 01 · Material Balance — (a), (b), (c), (d)
 *         - Sub-tab 02 · Specific Embedded Emissions — i–vii + justification
 *    7. 5-row "Total purchased levels" table → first 2 rows + "show all"
 *       expander. 10-row "Consumed in processes" table → same pattern.
 *    8. Inline title-edit field moved OUT of the accordion summary (cluttered
 *       UX) INTO a clean labelled input at the top of the precursor panel.
 *    9. DUPLICATE (c)/(d) block in original code — REMOVED. The original
 *       had both a single-row (c)/(d) AND a 2-row .map() loop rendering
 *       the same data twice. Only the single computed-row version is kept.
 *   10. Sticky right panel — soft-card + accent-stripe (matches Form B/C/D
 *       banners). Shows: SEE direct, SEE indirect, total direct em.,
 *       total indirect em., SEE grand total (large CBAM navy).
 *   11. Color palette unified with A/B/C/D:
 *         - white = user-fill
 *         - lblue (#eff6ff) = auto-filled / auto-calculated / read-only
 *         - lgrey (#f1f5f9) = disabled
 *       Old palette (D4EDC4 green, FFFFB3 yellow, C6EFCE readonly,
 *       E7F2E3 lgreen, e8f4ff autoLinked, F0F0F0 panel) entirely gone.
 *   12. Sector context chip at top — FYI only, same pattern as Form C.
 *       Does NOT gate any input. All SEE fields editable in all sectors.
 *   13. Control balance (d) restyled as soft-card pass/fail (green/red),
 *       matching Form D.
 *   14. TRUE/FALSE language gone (wasn't relevant here, just confirming
 *       no toggle-switch needed for Form E).
 *   15. Modal delete confirmation preserved (destructive action).
 *   16. NEXT button between precursors; Save & Continue on final precursor
 *       → navigates to /form-f/:auditId.
 *
 *  Behaviour preserved EXACTLY:
 *    - scaffoldProcess helpers: createPrecursorId, applyDerivedFields,
 *      createEmptyPrecursor, buildPrecLabel, autoMatchProcessForPrecursor,
 *      seedFromFormA, mergeStoredWithSeed, normalizePrecursor
 *    - CBAM_PRECURSORS_BY_GOODS, ROUTES_BY_CATEGORY_E (unchanged)
 *    - E.applyDerivedFields engine call on every update
 *    - updatePrecursorField path-splitting handler (unchanged)
 *    - calculateFormEProgress / getPrecursorProgress (unchanged)
 *    - Firestore onSnapshot merge logic (preserves all formAPrecursors-driven
 *      scaffolding) — exactly as in original
 *    - saveToFirestore payload shape ({ precursors, progressMeta, updatedAt })
 *    - Auto-save debounce (1.5s) preserved
 *    - Navigation routes /form-d/ (back) and /form-f/ (forward)
 **/

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { firestore as db } from "./firebase.config";
import { doc, setDoc, onSnapshot } from "firebase/firestore";
import { useFormContext } from "../context/FormContext";
import { E } from "../utils/CbamFormulas";
import AuditSidebar from "../components/AuditSidebar";
import useAuditProgress from "../hooks/useAuditProgress";

import {
  calculateRequiredProgressForRows,
  FORM_E_REQUIRED_FIELDS,
} from "../utils/CbamProgress";

import { UNIT_GROUPS, UNITS } from "../utils/Cbamunits";
import SEE_DEFAULTS from "../data/seeDefaults.json";
// ─── Sector-context labels (FYI only — never gates any input) ────────────────
//  Same rationale as Form C: Form E is a SEE calculation surface, not a
//  pricing surface. Users may report direct + indirect SEE for any precursor
//  regardless of installation sector.
const SECTORS_PRICED_INDIRECT = new Set(["Cement", "Fertilisers"]);

// ─── Static reference data (UNCHANGED from original) ─────────────────────────
const SourceOptionsDirect = ["Measured", "Default", "Unknown"];
const SourceOptionsEEF = [
  "D.4(a)",
  "D.4(b)",
  "D.4.1",
  "D.4.2",
  "D.4.3.1",
  "D.4.3.2",
  "Mix",
  "Unknown",
];
const JustificationOptions = [
  "Unreasonable costs for more accurate monitoring",
  "Data gaps",
  "Other",
];

const CBAM_PRECURSORS_BY_GOODS = {
  Cement: ["Cement clinker", "Calcined clays"],
  "Crude steel": [
    "Pig iron",
    "Direct reduced iron",
    "Alloys (FeMn, FeCr, FeNi)",
  ],
  "Iron or steel products": ["Crude steel", "Pig iron", "Direct reduced iron"],
  "Direct reduced iron": ["Sintered Ore", "Hydrogen"],
  "Pig iron": ["Sintered Ore", "Alloys (FeMn, FeCr, FeNi)"],
  "Alloys (FeMn, FeCr, FeNi)": ["Sintered Ore"],
  Ammonia: ["Hydrogen"],
  "Nitric acid": ["Ammonia"],
  Urea: ["Ammonia"],
  "Mixed fertilisers": ["Ammonia", "Nitric acid", "Urea"],
  "Aluminium products": ["Unwrought aluminium"],
};

const ROUTES_BY_CATEGORY_E = {
  Cement: ["All production routes"],
  "Cement clinker": ["All production routes"],
  "Calcined clays": ["All production routes"],
  "Aluminous cement": ["All production routes"],
  "Iron or steel products": ["All production routes"],
  "Crude steel": [
    "Basic oxygen steelmaking",
    "Electric arc furnace",
    "Other production routes",
    "Unknown production routes",
  ],
  "Direct reduced iron": ["All production routes"],
  "Pig iron": [
    "Blast furnace route",
    "Smelting reduction",
    "Other production routes",
    "Unknown production routes",
  ],
  "Alloys (FeMn, FeCr, FeNi)": ["All production routes"],
  "Sintered Ore": ["All production routes"],
  Hydrogen: [
    "Steam reforming and partial oxidation",
    "Electrolysis of water",
    "Chlor-Alkali electrolysis and production of chlorates",
    "Other production routes",
    "Unknown production routes",
  ],
  Ammonia: [
    "Haber-Bosch process with steam reforming of natural gas or biogas",
    "Haber-Bosch process with gasification of coal or other fuels",
    "Other production routes",
    "Unknown production routes",
  ],
  "Nitric acid": ["All production routes"],
  Urea: ["All production routes"],
  "Mixed fertilisers": ["All production routes"],
  "Aluminium products": ["All production routes"],
  "Unwrought aluminium": [
    "Primary (electrolytic) smelting",
    "Secondary melting (recycling)",
    "Other production routes",
    "Unknown production routes",
  ],
  Electricity: ["All production routes"],
};

// ─── Precursor scaffolding helpers (UNCHANGED) ───────────────────────────────
const createPrecursorId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `precursor-${Date.now()}-${Math.floor(Math.random() * 100000)}`;

const applyDerivedFields = (precursor, reportingYear = new Date().getFullYear()) => {
  const withFormulas = E.applyDerivedFields(precursor);

  // ── Case 2 override for SEE indirect ──────────────────────────────────
  // The EU default-values dataset gives ONE combined indirect emissions
  // number per CN code — it does NOT split into electricity consumption ×
  // emission factor the way fields (ii)/(iii) do. So when either of those
  // sources is set to a default (non-"Measured") value, skip the ii × iii
  // calculation and use the dataset's combined value directly instead.
  const { secIndirectSource, eefIndirectSource, cnCode } = withFormulas.emissions;
  const indirectIsDefault =
    isDefaultSource(secIndirectSource) || isDefaultSource(eefIndirectSource);

  const sourceSeeDirect = withFormulas.emissions.sourceSeeDirect;
  const defaultSeeDirect = isDefaultSource(sourceSeeDirect)
    ? getDefaultSEEValue(cnCode, "seeDirect", reportingYear)
    : withFormulas.emissions.seeDirect;

  const sourceSecIndirect = withFormulas.emissions.secIndirectSource;
  const defaultSecIndirect = isDefaultSource(sourceSecIndirect)
    ? getDefaultSEEValue(cnCode, "secIndirect", reportingYear)
    : withFormulas.emissions.secIndirect;

  const sourceEefIndirect = withFormulas.emissions.eefIndirectSource;
  const defaultEefIndirect = isDefaultSource(sourceEefIndirect)
    ? getDefaultSEEValue(cnCode, "eefIndirect", reportingYear)
    : withFormulas.emissions.eefIndirect;

  const withDefaultEmissions = {
    ...withFormulas.emissions,
    seeDirect: defaultSeeDirect,
    secIndirect: defaultSecIndirect,
    eefIndirect: defaultEefIndirect,
  };

  if (indirectIsDefault) {
    const defaultIndirect = getDefaultSEEValue(cnCode, "seeIndirect", reportingYear);
    const qty = E.totalPrecursorQuantity(withFormulas.totalPurchasedLevels || []);
    const totalIndirect = qty * Number(defaultIndirect || 0);
    return {
      ...withFormulas,
      emissions: {
        ...withDefaultEmissions,
        seeIndirect: defaultIndirect,
        totalIndirectEmissions: String(
          Math.round((totalIndirect + Number.EPSILON) * 1e6) / 1e6,
        ),
      },
    };
  }

  return {
    ...withFormulas,
    emissions: withDefaultEmissions,
  };
};

const createEmptyPrecursor = (overrides = {}) => ({
  id: createPrecursorId(),
  formAPrecursorId: "",
  title: "",
  countryCode: "",
  goodsCategory: "",
  linkedProcessId: "",
  originRoutes: [],
  totalPurchasedLevels: Array.from({ length: 5 }).map((_, i) => ({
    totalPurchasedLevel: "",
    productionRoute: overrides.originRoutes?.[i] || "",
    unit: "",
    amounts: "",
  })),
  totalPurchaseConsumption: "",
  consumedInProcesses: Array.from({ length: 10 }).map(() => ({
    processId: "",
    process: "",
    unit: "",
    amounts: "",
  })),
  consumedOtherPurposes: [
    { unit: "", amounts: "" },
    { unit: "", amounts: "" },
  ],
  control: [
    { unit: "", amounts: "" },
    { unit: "", amounts: "" },
  ],
  emissions: {
    seeDirect: "",
    sourceSeeDirect: "",
    secIndirect: "",
    secIndirectSource: "",
    eefIndirect: "",
    eefIndirectSource: "",
    seeIndirect: "",
    totalDirectEmissions: "",
    totalIndirectEmissions: "",
    justification: "",
    justificationOther: "",
    cnCode: "",
  },
  _materialBalanceCheck: { balance: 0, isValid: true, message: "" },
  _dataQualityComplete: false,
  ...overrides,
});

const buildPrecLabel = (fp) =>
  [fp.name, fp.goodsCategory].filter(Boolean).join(" | ");

const autoMatchProcessForPrecursor = (
  precursorGoodsCategory,
  formDProcesses,
) => {
  if (!precursorGoodsCategory || !formDProcesses?.length) return "";
  const directMatch = formDProcesses.find(
    (proc) => proc.aggregatedGoodsCategory === precursorGoodsCategory,
  );
  if (directMatch) return directMatch.processId || directMatch.id || "";
  const precursorMatch = formDProcesses.find((proc) => {
    const validPrecursors =
      CBAM_PRECURSORS_BY_GOODS[proc.aggregatedGoodsCategory] || [];
    return validPrecursors.includes(precursorGoodsCategory);
  });
  if (precursorMatch)
    return precursorMatch.processId || precursorMatch.id || "";
  return "";
};

const seedFromFormA = (formAPrecursor, formDProcesses = []) => {
  const formARoutes = [
    formAPrecursor.route1,
    formAPrecursor.route2,
    formAPrecursor.route3,
    formAPrecursor.route4,
    formAPrecursor.route5,
  ].filter(Boolean);
  const fallbackRoutes =
    ROUTES_BY_CATEGORY_E[formAPrecursor.goodsCategory] || [];
  const originRoutes = formARoutes.length > 0 ? formARoutes : fallbackRoutes;
  const precLabel = buildPrecLabel(formAPrecursor);
  const autoMatchedProcessId =
    formAPrecursor.linkedProcessId ||
    autoMatchProcessForPrecursor(formAPrecursor.goodsCategory, formDProcesses);

  const consumedInProcesses = Array.from({ length: 10 }).map((_, i) => ({
    processId: i === 0 && autoMatchedProcessId ? autoMatchedProcessId : "",
    process: "",
    unit: "",
    amounts: "",
  }));

  const precursor = createEmptyPrecursor({
    formAPrecursorId: formAPrecursor.id || "",
    title: formAPrecursor.name || "",
    countryCode: formAPrecursor.countryCode || "",
    goodsCategory: formAPrecursor.goodsCategory || "",
    linkedProcessId: autoMatchedProcessId,
    originRoutes,
    totalPurchasedLevels: Array.from({ length: 5 }).map((_, i) => ({
      totalPurchasedLevel: originRoutes[i] ? precLabel : "",
      productionRoute: originRoutes[i] || "",
      unit: "",
      amounts: "",
    })),
    consumedInProcesses,
  });

  // Auto-fill CN code from the matching Form A precursor row.
  precursor.emissions.cnCode = formAPrecursor.cnCode || "";
  return precursor;
};

const mergeStoredWithSeed = (fp, stored, formDProcesses = [], reportingYear) => {
const seed = seedFromFormA(fp, formDProcesses);
  if (!stored) return applyDerivedFields(seed, reportingYear);

  const mergedLevels = seed.totalPurchasedLevels.map((row, i) => {
    const saved = (stored.totalPurchasedLevels || [])[i] || {};
    return {
      ...row,
      totalPurchasedLevel: row.totalPurchasedLevel,
      productionRoute: row.productionRoute,
      unit: saved.unit || "",
      amounts: saved.amounts || "",
    };
  });

  const autoMatchedProcessId =
    fp.linkedProcessId ||
    autoMatchProcessForPrecursor(fp.goodsCategory, formDProcesses);
  const storedConsumed = stored.consumedInProcesses || [];

  const mergedConsumed = Array.from({ length: 10 }).map((_, i) => {
    const savedRow = storedConsumed[i] || {};
    if (savedRow.processId) {
      return {
        processId: savedRow.processId,
        process: savedRow.process || "",
        unit: savedRow.unit || "",
        amounts: savedRow.amounts || "",
      };
    }
    if (i === 0 && autoMatchedProcessId) {
      return {
        processId: autoMatchedProcessId,
        process: "",
        unit: savedRow.unit || "",
        amounts: savedRow.amounts || "",
      };
    }
    return {
      processId: "",
      process: "",
      unit: savedRow.unit || "",
      amounts: savedRow.amounts || "",
    };
  });

  // CN code priority: preserve the stored value unless the row is
  // using default/unknown sources, in which case it should follow Form A.
  const mergedEmissions = {
    ...createEmptyPrecursor().emissions,
    ...(stored.emissions || {}),
  };
  const rowUsesDefaultSource =
    isDefaultSource(mergedEmissions.sourceSeeDirect) ||
    isDefaultSource(mergedEmissions.secIndirectSource) ||
    isDefaultSource(mergedEmissions.eefIndirectSource);
  if (fp.cnCode && (!mergedEmissions.cnCode || rowUsesDefaultSource)) {
    mergedEmissions.cnCode = fp.cnCode;
  }

  return applyDerivedFields({
    ...seed,
    ...stored,
    // (rest of the object stays exactly the same — only the outer call below changes)
    formAPrecursorId: fp.id,
    title: fp.name || stored.title || "",
    countryCode: fp.countryCode || stored.countryCode || "",
    goodsCategory: fp.goodsCategory || stored.goodsCategory || "",
    linkedProcessId: autoMatchedProcessId || stored.linkedProcessId || "",
    originRoutes: seed.originRoutes,
    totalPurchasedLevels: mergedLevels,
    consumedInProcesses: mergedConsumed,
    emissions: mergedEmissions,
    _materialBalanceCheck: {
      ...createEmptyPrecursor()._materialBalanceCheck,
      ...(stored._materialBalanceCheck || {}),
    },
    _dataQualityComplete:
      typeof stored._dataQualityComplete === "boolean"
        ? stored._dataQualityComplete
        : false,
  }, reportingYear);
};

const normalizePrecursor = (prec, reportingYear) => {
  const base = createEmptyPrecursor();
  return applyDerivedFields({
    ...base,
    ...prec,
    id: prec?.id || base.id,
    formAPrecursorId: prec?.formAPrecursorId || "",
    title: prec?.title || "",
    countryCode: prec?.countryCode || "",
    goodsCategory: prec?.goodsCategory || "",
    linkedProcessId: prec?.linkedProcessId || "",
    originRoutes: prec?.originRoutes || [],
    totalPurchasedLevels: Array.isArray(prec?.totalPurchasedLevels)
      ? prec.totalPurchasedLevels.map((row, i) => ({
          ...base.totalPurchasedLevels[i],
          totalPurchasedLevel: row?.totalPurchasedLevel || "",
          productionRoute: row?.productionRoute || "",
          unit: row?.unit || "",
          amounts: row?.amounts || "",
        }))
      : base.totalPurchasedLevels,
    consumedInProcesses: Array.isArray(prec?.consumedInProcesses)
      ? prec.consumedInProcesses.map((row, i) => ({
          ...base.consumedInProcesses[i],
          processId: row?.processId || "",
          process: row?.process || "",
          unit: row?.unit || "",
          amounts: row?.amounts || "",
        }))
      : base.consumedInProcesses,
    consumedOtherPurposes: Array.isArray(prec?.consumedOtherPurposes)
      ? prec.consumedOtherPurposes.map((row, i) => ({
          ...base.consumedOtherPurposes[i],
          ...row,
        }))
      : base.consumedOtherPurposes,
    control: Array.isArray(prec?.control)
      ? prec.control.map((row, i) => ({ ...base.control[i], ...row }))
      : base.control,
    emissions: { ...base.emissions, ...(prec?.emissions || {}) },
    _materialBalanceCheck: {
      ...base._materialBalanceCheck,
      ...(prec?._materialBalanceCheck || {}),
    },
    _dataQualityComplete:
      typeof prec?._dataQualityComplete === "boolean"
        ? prec._dataQualityComplete
        : base._dataQualityComplete,
  }, reportingYear);
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  .fe { font-family: 'IBM Plex Sans', sans-serif; font-size: 13px; color: #161616; }

  /* Slim header */
  .fe-header-slim {
    background: #fff;
    border-bottom: 2px solid #2563eb;
    padding: 14px clamp(20px, 3vw, 32px);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .fe-header-slim .title {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.2px;
  }
  .fe-body {
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    padding: 14px clamp(20px, 3vw, 32px) 20px;
  }

  /* Sector chip strip */
  .fe-sector-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    color: #475569;
    margin-bottom: 10px;
    flex-wrap: wrap;
  }
  .fe-sector-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #eff6ff;
    color: #1e40af;
    border: 1px solid #bfdbfe;
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: 600;
    font-size: 11px;
  }
  .fe-sector-note { font-style: italic; color: #64748b; }

  /* Precursor tabs */
  .fe-tabs {
    display: flex;
    gap: 4px;
    border-bottom: 2px solid #2563eb;
    margin-bottom: 14px;
    overflow-x: auto;
  }
  .fe-tab {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    padding: 9px 16px;
    font-size: 13px;
    color: #64748b;
    cursor: pointer;
    border: 1px solid transparent;
    border-bottom: none;
    background: #fff;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    white-space: nowrap;
    user-select: none;
    transition: background 0.15s, color 0.15s;
  }
  .fe-tab:hover:not(.active) { background: #f1f5f9; color: #1e293b; }
  .fe-tab.active {
    color: #0f172a;
    font-weight: 700;
    border-color: #cbd5e1;
    background: #fff;
  }
  .fe-tab-num {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 11px;
    font-weight: 700;
    background: #e2e8f0;
    color: #475569;
    padding: 2px 7px;
    border-radius: 4px;
    letter-spacing: 0.5px;
  }
  .fe-tab.active .fe-tab-num { background: #2563eb; color: #fff; }
  .fe-tab-title { max-width: 180px; overflow: hidden; text-overflow: ellipsis; }
  .fe-tab-chip {
    background: #eff6ff;
    color: #1e40af;
    border: 1px solid #bfdbfe;
    padding: 0 6px;
    border-radius: 3px;
    font-size: 9px;
    font-weight: 700;
  }
  .fe-tab-badge {
    padding: 2px 8px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 700;
    background: #E0E0E0;
    color: #555;
  }
  .fe-tab-badge.done  { background: #DCFCE7; color: #15803D; }
  .fe-tab-badge.warn  { background: #FEF3C7; color: #D97706; }
  .fe-tab-badge.empty { background: #FEE2E2; color: #DC2626; }
  .fe-tab-add {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 9px 14px;
    font-size: 12px;
    color: #2563eb;
    font-weight: 600;
    cursor: pointer;
    border: 1px dashed #93c5fd;
    border-bottom: none;
    background: transparent;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    white-space: nowrap;
    transition: background 0.15s;
  }
  .fe-tab-add:hover { background: #eff6ff; }

  /* Two-column layout */
  .fe-grid {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 16px;
  }
  @media (max-width: 1100px) {
    .fe-grid { grid-template-columns: 1fr; }
  }
  .fe-content { min-width: 0; }
  .fe-sticky-col { position: relative; }
  .fe-sticky {
    position: sticky;
    top: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  /* Precursor meta header — title input + chips */
  .fe-prec-meta {
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 12px 14px;
    margin-bottom: 12px;
  }
  .fe-prec-meta-row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 10px;
    align-items: end;
  }
  .fe-prec-meta-title-lbl {
    font-size: 9px;
    color: #64748b;
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 0.5px;
    margin-bottom: 3px;
  }
  .fe-prec-meta-input {
    border: 1px solid #cbd5e1;
    background: #fff;
    padding: 7px 10px;
    border-radius: 4px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #161616;
    outline: none;
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.15s;
  }
  .fe-prec-meta-input:focus { border-color: #2563eb; }
  .fe-prec-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed #e2e8f0;
    font-size: 10px;
    color: #64748b;
  }
  .fe-prec-chip {
    background: #eff6ff;
    color: #1e40af;
    border: 1px solid #bfdbfe;
    padding: 1px 7px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 500;
  }
  .fe-prec-chip-country { background: #DCFCE7; color: #15803D; border-color: #86efac; }
  .fe-prec-chip-process { background: #fef3c7; color: #92400e; border-color: #fde68a; }

  /* Sub-tabs */
  .fe-subtabs {
    display: flex;
    gap: 4px;
    border-bottom: 1px solid #cbd5e1;
    margin-bottom: 12px;
  }
  .fe-subtab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    font-size: 12px;
    color: #64748b;
    cursor: pointer;
    border: 1px solid transparent;
    border-bottom: none;
    background: #fff;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    white-space: nowrap;
    user-select: none;
    transition: background 0.15s, color 0.15s;
  }
  .fe-subtab:hover:not(.active) { background: #f8fafc; color: #1e293b; }
  .fe-subtab.active {
    color: #0f172a;
    font-weight: 700;
    border-color: #cbd5e1;
    border-bottom: 1px solid #fff;
    background: #fff;
    margin-bottom: -1px;
  }
  .fe-subtab-num {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 10px;
    font-weight: 700;
    background: #e2e8f0;
    color: #475569;
    padding: 1px 6px;
    border-radius: 3px;
    letter-spacing: 0.4px;
  }
  .fe-subtab.active .fe-subtab-num { background: #2563eb; color: #fff; }
  .fe-subtab-meta { font-size: 10px; color: #94a3b8; font-weight: 500; }
  .fe-subtab-panel {
    background: #fff;
    border: 1px solid #cbd5e1;
    border-top: none;
    border-radius: 0 0 6px 6px;
    padding: 14px;
    margin-bottom: 12px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  }

  /* Sub-label */
  .fe-sub-label {
    font-size: 11px;
    font-weight: 700;
    color: #1e293b;
    margin: 12px 0 6px;
    padding-bottom: 3px;
    border-bottom: 2px solid #2563eb;
    display: inline-block;
  }
  .fe-sub-label:first-child { margin-top: 0; }
  .fe-sub-label .hint {
    font-size: 9px;
    color: #64748b;
    font-weight: 500;
    margin-left: 6px;
    letter-spacing: 0;
    text-transform: none;
  }

  /* Row tables */
  .fe-rows {
    display: grid;
    gap: 4px;
    margin-bottom: 6px;
  }
  .fe-row-head, .fe-row {
    display: grid;
    gap: 6px;
    align-items: center;
  }
  .fe-row-head {
    font-size: 9px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 0 4px;
  }
  .fe-row { padding: 2px 0; }
  .fe-rownum {
    text-align: center;
    color: #94a3b8;
    font-size: 10px;
    font-family: 'IBM Plex Mono', monospace;
  }

  /* Inputs - 3 colour states */
  .fe-input, .fe-select {
    border: 1px solid #cbd5e1;
    background: #fff;
    padding: 6px 8px;
    border-radius: 4px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px;
    color: #161616;
    outline: none;
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.15s, background 0.15s;
  }
  .fe-input:focus, .fe-select:focus { border-color: #2563eb; }
  .fe-input.lblue, .fe-select.lblue {
    background: #eff6ff;
    border-color: #bfdbfe;
  }
  .fe-input.lblue:read-only { cursor: default; }
  .fe-input.lgrey, .fe-select.lgrey {
    background: #f1f5f9;
    border-color: #e2e8f0;
    color: #94a3b8;
    cursor: not-allowed;
  }
  .fe-textarea {
    width: 100%;
    min-height: 60px;
    border: 1px solid #cbd5e1;
    background: #fff;
    border-radius: 4px;
    padding: 6px 8px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px;
    color: #161616;
    resize: vertical;
    outline: none;
    transition: border-color 0.15s;
  }
  .fe-textarea:focus { border-color: #2563eb; }

  /* Show-all expander */
  .fe-expander {
    background: transparent;
    border: 1px dashed #cbd5e1;
    color: #64748b;
    padding: 6px 12px;
    font-size: 11px;
    font-weight: 600;
    border-radius: 4px;
    cursor: pointer;
    width: 100%;
    text-align: center;
    transition: background 0.15s, color 0.15s;
  }
  .fe-expander:hover {
    background: #eff6ff;
    border-color: #93c5fd;
    color: #2563eb;
  }

  /* Single-row field */
  .fe-field {
    display: grid;
    grid-template-columns: 1fr 100px 1fr;
    gap: 8px;
    align-items: center;
    padding: 4px 0;
  }
  .fe-field-label {
    font-size: 12px;
    color: #475569;
    font-weight: 500;
  }
  .fe-field-unit {
    font-size: 10px;
    color: #64748b;
    text-align: center;
    background: #f1f5f9;
    padding: 4px 6px;
    border-radius: 3px;
  }

  /* Control balance card */
  .fe-control {
    border: 1px solid;
    border-radius: 5px;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 10px;
  }
  .fe-control.ok  {
    background: #f0fdf4;
    border-color: #86efac;
    color: #15803d;
  }
  .fe-control.bad {
    background: #fef2f2;
    border-color: #fca5a5;
    color: #b91c1c;
  }
  .fe-control .label {
    font-size: 11px;
    font-weight: 600;
  }
  .fe-control .formula {
    font-size: 10px;
    color: #64748b;
    margin-top: 2px;
  }
  .fe-control .val {
    font-family: 'IBM Plex Mono', monospace;
    font-weight: 700;
    font-size: 15px;
  }

  /* Emissions table (SEE sub-tab) */
  .fe-em-table {
    border: 1px solid #cbd5e1;
    border-radius: 5px;
    overflow: hidden;
  }
  .fe-em-row-head, .fe-em-row {
    display: grid;
    grid-template-columns: 2fr 90px 1.2fr 1.2fr;
    align-items: stretch;
  }
  .fe-em-row-head {
    background: #f8fafc;
    border-bottom: 1px solid #cbd5e1;
    font-size: 9px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .fe-em-row-head > div {
    padding: 8px 10px;
    border-right: 1px solid #e2e8f0;
  }
  .fe-em-row-head > div:last-child { border-right: none; }
  .fe-em-row {
    border-bottom: 1px solid #f1f5f9;
  }
  .fe-em-row:last-child { border-bottom: none; }
  .fe-em-row > div {
    padding: 6px 8px;
    border-right: 1px solid #f1f5f9;
    display: flex;
    align-items: center;
  }
  .fe-em-row > div:last-child { border-right: none; }
  .fe-em-label {
    font-size: 12px;
    color: #475569;
    line-height: 1.3;
  }
  .fe-em-label .hint {
    font-size: 10px;
    color: #2563eb;
    margin-left: 4px;
    font-style: italic;
  }
  .fe-em-unit {
    font-size: 10px;
    color: #64748b;
    background: #f1f5f9;
    border-radius: 3px;
    padding: 4px 6px;
    text-align: center;
    width: 100%;
  }

  /* Form A info banner inside sub-tab 01 */
  .fe-info-banner {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 10px;
    color: #1e40af;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    padding: 6px 10px;
    border-radius: 4px;
    margin-bottom: 10px;
  }

  /* Sticky right SEE panel */
  .fe-see-card {
    position: relative;
    background: linear-gradient(180deg, #f8fbff 0%, #eff6ff 100%);
    border: 1px solid #dbeafe;
    border-radius: 6px;
    padding: 14px 14px 14px 20px;
    overflow: hidden;
  }
  .fe-see-card::before {
    content: "";
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, #1e40af 0%, #2563eb 50%, #06b6d4 100%);
  }
  .fe-see-card-title {
    font-size: 9px;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    font-weight: 700;
    margin-bottom: 10px;
  }
  .fe-see-item {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 5px 0;
    border-bottom: 1px solid rgba(203,213,225,0.5);
  }
  .fe-see-item:last-of-type { border-bottom: none; }
  .fe-see-item .k {
    font-size: 10px;
    color: #475569;
    font-weight: 600;
  }
  .fe-see-item .v {
    font-family: 'IBM Plex Mono', monospace;
    font-weight: 700;
    font-size: 13px;
    color: #1e293b;
  }
  .fe-see-item .v.zero { opacity: 0.4; }
  .fe-see-item .unit {
    font-size: 9px;
    color: #94a3b8;
    margin-left: 4px;
  }
  .fe-see-grand {
    border-top: 1px solid #cbd5e1;
    margin-top: 10px;
    padding-top: 10px;
  }
  .fe-see-grand .k {
    font-size: 10px;
    color: #475569;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }
  .fe-see-grand .v {
    font-family: 'IBM Plex Mono', monospace;
    font-weight: 700;
    font-size: 22px;
    color: #1e40af;
    letter-spacing: -0.4px;
  }
  .fe-see-grand .v.zero { color: #94a3b8; }
  .fe-see-grand .unit { font-size: 10px; color: #64748b; margin-left: 4px; }

  /* Footer / buttons */
  .fe-footer-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #e2e8f0;
  }
  .fe-btn-prev {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 9px 18px;
    background: #fff; color: #475569;
    border: 1px solid #cbd5e1;
    font-family: inherit; font-size: 13px; font-weight: 600;
    cursor: pointer; border-radius: 4px;
  }
  .fe-btn-prev:hover:not(:disabled) { background: #f1f5f9; }
  .fe-btn-prev:disabled { opacity: 0.4; cursor: not-allowed; }
  .fe-btn-save {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 10px 24px;
    background: #2563eb; color: #fff;
    border: none;
    font-family: inherit; font-size: 14px; font-weight: 700;
    cursor: pointer; border-radius: 4px;
    transition: background 0.15s;
  }
  .fe-btn-save:hover { background: #1d4ed8; }
  .fe-btn-save:disabled { background: #cbd5e1; cursor: not-allowed; }
  .fe-btn-delete {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 6px 10px;
    background: transparent; color: #dc2626;
    border: 1px solid #fca5a5;
    font-family: inherit; font-size: 11px; font-weight: 600;
    cursor: pointer; border-radius: 4px;
    transition: background 0.15s;
  }
  .fe-btn-delete:hover:not(:disabled) { background: #fef2f2; }
  .fe-btn-delete:disabled { opacity: 0.4; cursor: not-allowed; }

  /* Toast */
  .fe-toast {
    position: fixed; top: 88px; right: 20px; z-index: 1400;
    background: #22c55e; color: #fff;
    padding: 12px 20px; font-size: 13px; font-weight: 600;
    border-radius: 6px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  }
  .fe-toast.error { background: #dc2626; }

  /* Empty state */
  .fe-empty {
    display: flex; flex-direction: column; align-items: center;
    gap: 12px; padding: 80px 20px; text-align: center;
  }
  .fe-empty-title { font-size: 17px; color: #475569; font-weight: 600; }
  .fe-empty-sub   { font-size: 13px; color: #64748b; }
`;

// ─── Small helpers ────────────────────────────────────────────────────────────
const fmt = (v) => {
  if (v === "" || v === null || v === undefined) return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return n.toLocaleString("en-US", { maximumFractionDigits: 4 });
};
// A source value other than "Measured" means the supplier didn't give real
// data, so a default/CN-code-based value must be used instead (Case 2).
const isDefaultSource = (src) => !!src && src !== "Measured";

// A precursor is "Case 2" if ANY of its three source dropdowns is non-Measured.
const getCaseForPrecursor = (prec) => {
  const { sourceSeeDirect, secIndirectSource, eefIndirectSource } =
    prec.emissions;
  const anyDefault =
    isDefaultSource(sourceSeeDirect) ||
    isDefaultSource(secIndirectSource) ||
    isDefaultSource(eefIndirectSource);
  return anyDefault ? "case2" : "case1";
};
// EU CBAM default values (India, per CN code) — sourced from
// CBAM_Default_Values.xlsx, now stored in src/data/seeDefaults.json.
// NOTE: the official dataset gives ONE combined "indirect emissions" value
// per CN code — it does not split into electricity-consumption ×
// emission-factor the way Form E's (ii) and (iii) fields do. So here,
// seeIndirect is used directly; secIndirect/eefIndirect are left blank
// since there's no official split to auto-fill them with.
// Determines the CBAM reporting year from Form A's reporting period start
// date. Falls back to the current calendar year if unavailable.
const getReportingYear = (formAData) => {
  const start = formAData?.reportingPeriodStart;
  if (!start) return new Date().getFullYear();
  const parsed = new Date(start);
  const year = parsed.getFullYear();
  return Number.isFinite(year) ? year : new Date().getFullYear();
};

// Returns the markup multiplier for a CN code at a given reporting year.
// The EU dataset only marks up the COMBINED total (not direct/indirect
// separately), so we derive a multiplier from (marked-up total ÷ base
// total) and apply it proportionally to both seeDirect and seeIndirect.
// Years before 2026 (pre-definitive-regime) get no markup (multiplier 1).
// Years 2028+ all use the "2028 and onwards" plateau value.
const getMarkupMultiplier = (cnCode, year) => {
  const entry = SEE_DEFAULTS[(cnCode || "").trim()];
  if (!entry || !entry.byYear || !entry.seeTotal) return 1;
  if (year < 2026) return 1;
  const yearKey = year >= 2028 ? "2028" : String(year);
  const markedTotal = entry.byYear[yearKey];
  if (markedTotal === undefined) return 1;
  return markedTotal / entry.seeTotal;
};

// Looks up the default value for a CN code + parameter, applying the
// year-appropriate markup for seeDirect and seeIndirect. Other parameter
// keys (if any are added later) are returned as-is, unmarked.
const getDefaultSEEValue = (cnCode, parameterKey, reportingYear) => {
  if (!cnCode) return "";
  const entry = SEE_DEFAULTS[cnCode.trim()];
  if (!entry || entry[parameterKey] === undefined) return "";

  const base = entry[parameterKey];
  if (parameterKey !== "seeDirect" && parameterKey !== "seeIndirect") {
    return String(base);
  }

  const multiplier = getMarkupMultiplier(cnCode, reportingYear ?? new Date().getFullYear());
  const marked = base * multiplier;
  return String(Math.round((marked + Number.EPSILON) * 1e6) / 1e6);
};
// ═════════════════════════════════════════════════════════════════════════════
const FormE = () => {
  const navigate = useNavigate();
  const { auditId } = useParams();
  const { formsData } = useAuditProgress(auditId);
  const { allFormData, updateForm } = useFormContext();

  const installSector = allFormData?.formA?.sector || "";
  const sectorPricesIndirect = SECTORS_PRICED_INDIRECT.has(installSector);
  const reportingYear = getReportingYear(allFormData?.formA);

  const formAPrecursors = allFormData?.formA?.purchasedPrecursors || [];
  const formDProcesses = useMemo(
    () => allFormData?.formD?.processes || [],
    [allFormData?.formD?.processes],
  );

  const processOptions = useMemo(() => {
    return formDProcesses.map((proc, index) => ({
      id: proc.processId || `process-${index}`,
      label:
        proc.title?.trim() ||
        proc.processId ||
        `Production process ${index + 1}`,
    }));
  }, [formDProcesses]);

  // ── State ──────────────────────────────────────────────────────────────────
  const [precursors, setPrecursors] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [subTab, setSubTab] = useState({}); // per-precursor sub-tab
  const [expanded, setExpanded] = useState({}); // per-precursor "show all" state
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const autoSaveRef = useRef(null);
  const lastLocalWriteRef = useRef(0);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const getSubTab = (idx) => subTab[idx] || "balance";
  const setSubTabFor = (idx, t) => setSubTab((p) => ({ ...p, [idx]: t }));

  const isExpanded = (idx, table) => !!expanded[`${idx}.${table}`];
  const toggleExpanded = (idx, table) =>
    setExpanded((p) => ({ ...p, [`${idx}.${table}`]: !p[`${idx}.${table}`] }));

  // ── Context sync (only after Firestore has settled) ──────────────────────
  useEffect(() => {
    if (isLoading) return;
    updateForm("formE", { precursors });
  }, [precursors, isLoading, updateForm]);

  // ── Auto-save (1.5s debounce — preserved from original) ──────────────────
 useEffect(() => {
    if (!auditId || precursors.length === 0 || isLoading) return;
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(async () => {
      try {
        lastLocalWriteRef.current = Date.now();
        const ref = doc(db, "auditReports", auditId, "form_e", "main");
        const progressMeta = calculateFormEProgress();
        await setDoc(
          ref,
          { precursors, progressMeta, updatedAt: new Date() },
          { merge: true }
        );
        updateForm("formE", { precursors, progressMeta });
      } catch (e) {
        console.warn("FormE auto-save failed:", e);
      }
    }, 1500);
    return () => {
      if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    };
  }, [precursors, auditId, isLoading]);

  // ── Firestore sync (preserved verbatim from original) ────────────────────
  useEffect(() => {
    if (!auditId) return;
    setPrecursors([]);
    setIsLoading(true);

    const docRef = doc(db, "auditReports", auditId, "form_e", "main");
    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        // Skip snapshots that arrive just after our own write — these are
        // an echo of what we already have on screen, and processing them
        // can overwrite anything the user typed in the meantime.
        if (Date.now() - lastLocalWriteRef.current < 2500) {
          return;
        }
        if (formAPrecursors.length > 0) {
          const stored = docSnap.exists()
            ? docSnap.data().precursors || []
            : [];
          const merged = formAPrecursors.map((fp) => {
            const storedPrec = stored.find((p) => p.formAPrecursorId === fp.id);
            return mergeStoredWithSeed(fp, storedPrec || null, formDProcesses, reportingYear);
          });
          setPrecursors(merged);
        } else if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.precursors?.length > 0) {
            setPrecursors(
              data.precursors.map((p) => normalizePrecursor(p, reportingYear)),
            );
          } else {
            setPrecursors([]);
          }
        } else {
          setPrecursors([]);
        }
        setIsLoading(false);
      },
      (error) => {
        console.error("FormE onSnapshot error:", error);
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auditId,
    JSON.stringify(
      formAPrecursors.map((fp) => fp.id + fp.goodsCategory + fp.name + fp.cnCode),
    ),
    JSON.stringify(formDProcesses.map((p) => p.processId)),
  ]);

  // ── Progress + save (PRESERVED) ──────────────────────────────────────────
  const getPrecursorProgress = (precursor) => {
    const fields =
      getCaseForPrecursor(precursor) === "case2"
        ? [
            ...FORM_E_REQUIRED_FIELDS.alwaysRequired,
            ...FORM_E_REQUIRED_FIELDS.case2Conditional,
          ]
        : FORM_E_REQUIRED_FIELDS.alwaysRequired;
    return calculateRequiredProgressForRows([precursor], fields);
  };

  const calculateFormEProgress = () => {
    const precursorSections = precursors.map((precursor, index) => ({
      key: `precursor_${index + 1}`,
      title: precursor.title?.trim() || `Purchased precursor ${index + 1}`,
      ...getPrecursorProgress(precursor),
    }));
    const editableTotal = precursorSections.reduce((s, x) => s + x.total, 0);
    const editableFilled = precursorSections.reduce((s, x) => s + x.filled, 0);
    const progress =
      editableTotal === 0
        ? 0
        : Math.round((editableFilled / editableTotal) * 100);
    const sections = precursorSections.reduce((acc, section) => {
      acc[section.key] = {
        title: section.title,
        filled: section.filled,
        total: section.total,
        progress: section.progress,
      };
      return acc;
    }, {});
    return {
      editableTotal,
      editableFilled,
      progress,
      status:
        progress >= 100
          ? "Completed"
          : progress > 0
            ? "In Progress"
            : "Not Started",
      sections,
    };
  };

  const saveToFirestore = async () => {
    if (!auditId) throw new Error("No auditId found");
    lastLocalWriteRef.current = Date.now();
    const docRef = doc(db, "auditReports", auditId, "form_e", "main");
    const progressMeta = calculateFormEProgress();
    const payload = { precursors, progressMeta, updatedAt: new Date() };
    await setDoc(docRef, payload, { merge: true });
    updateForm("formE", payload);
  };

  const handleSaveAndNext = async () => {
    if (!auditId) return;
    setIsSaving(true);
    try {
      await saveToFirestore();
      showToast("Form E saved");
      setTimeout(() => navigate(`/form-f/${auditId}`), 500);
    } catch (err) {
      console.error(err);
      showToast("Save failed", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = async () => {
    setIsSaving(true);
    try {
      await saveToFirestore();
      navigate(`/form-d/${auditId}`);
    } catch (err) {
      navigate(`/form-d/${auditId}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleNextPrecursor = async () => {
    setIsSaving(true);
    try {
      await saveToFirestore();
      if (activeIdx < precursors.length - 1) {
        setActiveIdx(activeIdx + 1);
        setSubTabFor(activeIdx + 1, "balance");
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      showToast("Saved");
    } catch (e) {
      console.error(e);
      showToast("Save failed", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddPrecursor = () => {
    setPrecursors((prev) => {
      const next = [...prev, createEmptyPrecursor()];
      setActiveIdx(next.length - 1);
      return next;
    });
  };

  const handleRemovePrecursor = (index) => setDeleteConfirm(index);

  const confirmDelete = () => {
    setPrecursors((prev) => {
      if (prev.length === 1) return prev;
      const next = prev.filter((_, i) => i !== deleteConfirm);
      setActiveIdx(0);
      return next;
    });
    setDeleteConfirm(null);
  };

  // ── update() — PRESERVED path-splitting handler ──────────────────────────
  const updatePrecursorField = (index, path, value) => {
    setPrecursors((prev) => {
      const copy = prev.map((p, idx) => (idx === index ? { ...p } : p));
      const parts = path.split(".");
      let cur = copy[index];
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = Array.isArray(cur[parts[i]])
          ? [...cur[parts[i]]]
          : { ...cur[parts[i]] };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      copy[index] = applyDerivedFields(copy[index], reportingYear);

    return copy;
    });
  };

  const tabBadgeClass = (pct) =>
    pct === 100 ? "done" : pct > 0 ? "warn" : "empty";

  // ── Loading + empty states ────────────────────────────────────────────────
  if (isLoading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );

  if (precursors.length === 0) {
    return (
      <>
        <style>{STYLES}</style>
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f8fafc" }}>
          <AuditSidebar displayAuditId={auditId} formsData={formsData} />
          <Box sx={{ flex: 1 }}>
            <div className="fe-header-slim">
              <div className="title">Purchased Precursors</div>
            </div>
            <div className="fe-empty">
              <div className="fe-empty-title">No purchased precursors yet.</div>
              <div className="fe-empty-sub">
                Add them in Form A → Section 5, or use the button below.
              </div>
              <button className="fe-btn-save" onClick={handleAddPrecursor}>
                + Add precursor
              </button>
              <button
                className="fe-btn-prev"
                onClick={() => navigate(`/form-a/${auditId}`)}
              >
                ← Go to Form A
              </button>
            </div>
          </Box>
        </Box>
      </>
    );
  }

  // ─── Render Material Balance sub-tab ───────────────────────────────────────
  const renderMaterialBalance = (prec, idx) => {
    const hasRoutes = prec.originRoutes?.length > 0;
    const autoMatchedId = prec.linkedProcessId || "";

    //  Control balance — same formula as original
    const totalPurchased = Number(prec.totalPurchaseConsumption) || 0;
    const consumedInProc = (prec.consumedInProcesses || []).reduce(
      (s, r) => s + (Number(r.amounts) || 0),
      0,
    );
    const consumedOther = Number(prec.consumedOtherPurposes?.[0]?.amounts) || 0;
    const balance = +(totalPurchased - consumedInProc - consumedOther).toFixed(
      4,
    );
    const isValid = Math.abs(balance) < 0.001;

    const purchasedRowsToShow = isExpanded(idx, "purchased") ? 5 : 2;
    const consumedRowsToShow = isExpanded(idx, "consumed") ? 10 : 2;

    return (
      <div className="fe-subtab-panel">
        {/* Form A info banner */}
        {prec.formAPrecursorId && (
          <div className="fe-info-banner">
            <InfoOutlinedIcon sx={{ fontSize: 13 }} />
            <span>
              Linked from Form A
              {prec.countryCode && (
                <>
                  {" "}
                  · country <strong>{prec.countryCode}</strong>
                </>
              )}
              {prec.goodsCategory && (
                <>
                  {" "}
                  · goods <strong>{prec.goodsCategory}</strong>
                </>
              )}
              {autoMatchedId && (
                <>
                  {" "}
                  · auto-linked to{" "}
                  <strong>
                    {processOptions.find((p) => p.id === autoMatchedId)
                      ?.label || autoMatchedId}
                  </strong>
                </>
              )}
            </span>
          </div>
        )}

        {/* (a) Total purchased levels */}
        <div className="fe-sub-label">
          (a) Total purchased levels
          {hasRoutes && (
            <span className="hint">routes auto-filled from Form A</span>
          )}
        </div>
        <div className="fe-rows">
          <div
            className="fe-row-head"
            style={{ gridTemplateColumns: "28px 1.5fr 1.5fr 110px 1fr" }}
          >
            <div className="fe-rownum">#</div>
            <div>Precursor</div>
            <div>Production route</div>
            <div>Unit</div>
            <div>Amounts</div>
          </div>
          {prec.totalPurchasedLevels
            .slice(0, purchasedRowsToShow)
            .map((row, i) => (
              <div
                key={i}
                className="fe-row"
                style={{ gridTemplateColumns: "28px 1.5fr 1.5fr 110px 1fr" }}
              >
                <div className="fe-rownum">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <input
                  className="fe-input lblue"
                  readOnly
                  value={row.totalPurchasedLevel || ""}
                />
                <input
                  className={`fe-input ${hasRoutes && row.productionRoute ? "lblue" : ""}`}
                  readOnly={hasRoutes && !!row.productionRoute}
                  value={row.productionRoute || ""}
                  onChange={(e) =>
                    updatePrecursorField(
                      idx,
                      `totalPurchasedLevels.${i}.productionRoute`,
                      e.target.value,
                    )
                  }
                />
                <select
                  className="fe-select"
                  value={row.unit || ""}
                  onChange={(e) =>
                    updatePrecursorField(
                      idx,
                      `totalPurchasedLevels.${i}.unit`,
                      e.target.value,
                    )
                  }
                >
                  <option value="">—</option>
                  {UNIT_GROUPS.MASS_OR_VOLUME.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <input
                  className="fe-input"
                  type="number"
                  value={row.amounts || ""}
                  onChange={(e) =>
                    updatePrecursorField(
                      idx,
                      `totalPurchasedLevels.${i}.amounts`,
                      e.target.value,
                    )
                  }
                  placeholder="0"
                />
              </div>
            ))}
        </div>
        {!isExpanded(idx, "purchased") && (
          <button
            className="fe-expander"
            onClick={() => toggleExpanded(idx, "purchased")}
          >
            {`+ Show all ${prec.totalPurchasedLevels.length} rows (${prec.totalPurchasedLevels.length - 2} more)`}
          </button>
        )}
        {isExpanded(idx, "purchased") && (
          <button
            className="fe-expander"
            onClick={() => toggleExpanded(idx, "purchased")}
          >
            − Collapse to first 2 rows
          </button>
        )}

        {/* Total purchase consumption — auto */}
        <div className="fe-field" style={{ marginTop: 8 }}>
          <div className="fe-field-label" style={{ fontWeight: 700 }}>
            Total purchase for possible consumption within installation
          </div>
          <div className="fe-field-unit">t</div>
          <input
            className="fe-input lblue"
            readOnly
            value={prec.totalPurchaseConsumption || ""}
          />
        </div>

        {/* (b) Consumed in production processes */}
        <div className="fe-sub-label">
          (b) Consumed in production processes
          <span className="hint">linked to Form D processes</span>
        </div>
        {processOptions.length === 0 && (
          <div
            className="fe-info-banner"
            style={{
              background: "#fef3c7",
              borderColor: "#fde68a",
              color: "#92400e",
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 13 }} />
            <span>
              No production processes found in Form D. Complete Form D first.
            </span>
          </div>
        )}
        <div className="fe-rows">
          <div
            className="fe-row-head"
            style={{ gridTemplateColumns: "28px 3fr 110px 1fr" }}
          >
            <div className="fe-rownum">#</div>
            <div>Process (from Form D)</div>
            <div>Unit</div>
            <div>Amounts</div>
          </div>
          {prec.consumedInProcesses
            .slice(0, consumedRowsToShow)
            .map((row, i) => {
              const isAutoLinked =
                i === 0 &&
                row.processId === autoMatchedId &&
                autoMatchedId !== "";
              return (
                <div
                  key={i}
                  className="fe-row"
                  style={{ gridTemplateColumns: "28px 3fr 110px 1fr" }}
                >
                  <div className="fe-rownum">
                    {String(i + 1).padStart(2, "0")}
                    {isAutoLinked && (
                      <span style={{ color: "#15803d", marginLeft: 2 }}>•</span>
                    )}
                  </div>
                  <select
                    className={`fe-select ${isAutoLinked ? "lblue" : ""}`}
                    value={row.processId || ""}
                    onChange={(e) => {
                      const selected = processOptions.find(
                        (p) => p.id === e.target.value,
                      );
                      updatePrecursorField(
                        idx,
                        `consumedInProcesses.${i}.processId`,
                        e.target.value,
                      );
                      updatePrecursorField(
                        idx,
                        `consumedInProcesses.${i}.process`,
                        selected?.label || "",
                      );
                    }}
                  >
                    <option value="">
                      {processOptions.length === 0
                        ? "No processes in Form D"
                        : "Select process..."}
                    </option>
                    {processOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.id} — {option.label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="fe-select"
                    value={row.unit || ""}
                    onChange={(e) =>
                      updatePrecursorField(
                        idx,
                        `consumedInProcesses.${i}.unit`,
                        e.target.value,
                      )
                    }
                  >
                    <option value="">—</option>
                    {UNIT_GROUPS.MASS_OR_VOLUME.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <input
                    className="fe-input"
                    type="number"
                    value={row.amounts || ""}
                    onChange={(e) =>
                      updatePrecursorField(
                        idx,
                        `consumedInProcesses.${i}.amounts`,
                        e.target.value,
                      )
                    }
                  />
                </div>
              );
            })}
        </div>
        {!isExpanded(idx, "consumed") && (
          <button
            className="fe-expander"
            onClick={() => toggleExpanded(idx, "consumed")}
          >
            + Show all 10 rows (8 more)
          </button>
        )}
        {isExpanded(idx, "consumed") && (
          <button
            className="fe-expander"
            onClick={() => toggleExpanded(idx, "consumed")}
          >
            − Collapse to first 2 rows
          </button>
        )}

        {/* (c) Consumed for other purposes — single row (duplicate .map() block REMOVED) */}
        <div className="fe-sub-label">(c) Consumed for other purposes</div>
        <div className="fe-field">
          <div className="fe-field-label">
            e.g. sold or used for non-CBAM goods
          </div>
          <select
            className="fe-select"
            value={prec.consumedOtherPurposes[0].unit || ""}
            onChange={(e) =>
              updatePrecursorField(
                idx,
                "consumedOtherPurposes.0.unit",
                e.target.value,
              )
            }
          >
            <option value="">—</option>
            {UNIT_GROUPS.MASS_OR_VOLUME.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <input
            className="fe-input"
            type="number"
            value={prec.consumedOtherPurposes[0].amounts || ""}
            onChange={(e) =>
              updatePrecursorField(
                idx,
                "consumedOtherPurposes.0.amounts",
                e.target.value,
              )
            }
          />
        </div>

        {/* (d) Control balance — pass/fail card */}
        <div className={`fe-control ${isValid ? "ok" : "bad"}`}>
          <div>
            <div className="label">
              (d) Control balance {isValid ? "passed" : "off"} —{" "}
              {isValid ? "all purchased accounted for" : "imbalance detected"}
            </div>
            <div className="formula">(a) − (b) − (c) — must equal 0</div>
          </div>
          <div className="val">
            {balance} {isValid ? "✓" : "✗"}
          </div>
        </div>
      </div>
    );
  };

  // ─── Render SEE sub-tab ────────────────────────────────────────────────────
  const renderSEE = (prec, idx) => {
    const currentCase = getCaseForPrecursor(prec);
    const indirectIsDefault =
      isDefaultSource(prec.emissions.secIndirectSource) ||
      isDefaultSource(prec.emissions.eefIndirectSource);
    return (
      <div className="fe-subtab-panel">
        <div
          className="fe-sub-label"
          style={{ display: "flex", alignItems: "center", gap: "6px" }}
        >
          <span>Specific Embedded Emissions (SEE)</span>

          <Tooltip
            arrow
            placement="right"
            title={
              <>
                <strong>Case 1 – Supplier data available</strong>
                <br />• Select <b>Measured</b> as the Source.
                <br />• Refer to <b>Annex III</b> for the electricity emission
                factor source.
                <br />
                <br />
                <strong>Case 2 – Supplier data unavailable</strong>
                <br />• Provide the <b>CN Code</b>.
              </>
            }
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: 18,
                color: "#2563eb",
                cursor: "pointer",
              }}
            />
          </Tooltip>
        </div>

        {currentCase === "case2" && (
          <>
            <div className="fe-field" style={{ marginBottom: 10 }}>
              <div className="fe-field-label" style={{ fontWeight: 700 }}>
                CN Code <span style={{ color: "#dc2626" }}>*</span>
                <span className="hint" style={{ marginLeft: 6, fontWeight: 400 }}>
                  required when supplier data is unavailable
                </span>
              </div>
              <div className="fe-field-unit">—</div>
              <input
                className={`fe-input ${!prec.emissions.cnCode ? "" : "lblue"}`}
                value={prec.emissions.cnCode || ""}
                onChange={(e) => {
                  const newCode = e.target.value;
                  updatePrecursorField(idx, "emissions.cnCode", newCode);

                  if (isDefaultSource(prec.emissions.sourceSeeDirect)) {
                    updatePrecursorField(
                      idx,
                      "emissions.seeDirect",
                      getDefaultSEEValue(newCode, "seeDirect", reportingYear),
                      getDefaultSEEValue(newCode, "seeDirect"),
                    );
                  }
                  if (isDefaultSource(prec.emissions.secIndirectSource)) {
                    updatePrecursorField(
                      idx,
                      "emissions.secIndirect",
                      getDefaultSEEValue(newCode, "secIndirect", reportingYear),
                      getDefaultSEEValue(newCode, "secIndirect"),
                    );
                  }
                  if (isDefaultSource(prec.emissions.eefIndirectSource)) {
                    updatePrecursorField(
                      idx,
                      "emissions.eefIndirect",
                      getDefaultSEEValue(newCode, "eefIndirect", reportingYear),
                      getDefaultSEEValue(newCode, "eefIndirect"),
                    );
                  }
                }}
                placeholder="e.g. 72021120"
              />
            </div>

            {prec.emissions.cnCode &&
              !SEE_DEFAULTS[prec.emissions.cnCode.trim()] && (
                <div style={{ fontSize: 10, color: "#dc2626", marginTop: -6, marginBottom: 10 }}>
                  No default value found for this CN code (dataset currently covers 10 test codes).
                </div>
              )}
          </>
        )}

        <div className="fe-em-table">
          {/* Header row */}
          <div className="fe-em-row-head">
            <div>Parameter</div>
            <div>Unit</div>
            <div>Value</div>
            <div>Source</div>
          </div>

          {/* i — SEE direct */}
          <div className="fe-em-row">
            <div className="fe-em-label">
              i · Specific embedded direct emissions (SEE direct)
              {isDefaultSource(prec.emissions.sourceSeeDirect) && prec.emissions.cnCode && (
                <span className="hint">
                  incl. {reportingYear}, mark-up applied
                </span>
              )}
            </div>
            <div>
              <span className="fe-em-unit">{UNITS.TCO2E_PER_T}</span>
            </div>
         <div>
              <input
              className={`fe-input ${isDefaultSource(prec.emissions.sourceSeeDirect) ? "lblue" : ""}`}
              type="number"
              value={prec.emissions.seeDirect}
              onChange={(e) =>
                updatePrecursorField(
                  idx,
                  "emissions.seeDirect",
                  e.target.value,
              ) 
             }
            />
            </div>
            <div>
              <select
                className="fe-select"
                value={prec.emissions.sourceSeeDirect || ""}
                onChange={(e) => {
                  const src = e.target.value;
                  updatePrecursorField(idx, "emissions.sourceSeeDirect", src);
                  if (isDefaultSource(src)) {
                    updatePrecursorField(
                      idx,
                      "emissions.seeDirect",
                      getDefaultSEEValue(prec.emissions.cnCode, "seeDirect", reportingYear),
                    );
                  }
                }}
              >
                <option value="">Source...</option>
                {SourceOptionsDirect.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ii — Specific electricity consumption */}
          <div className="fe-em-row">
            <div className="fe-em-label">
              ii · Specific electricity consumption (for SEE indirect)
            </div>
            <div>
              <span className="fe-em-unit">{UNITS.MWH_PER_T}</span>
            </div>
            <div>
              <input
                className={`fe-input ${isDefaultSource(prec.emissions.secIndirectSource) ? "lblue" : ""}`}
                type="number"
                value={prec.emissions.secIndirect}
                onChange={(e) =>
                  updatePrecursorField(
                    idx,
                    "emissions.secIndirect",
                    e.target.value,
                  )
                }
              />
            </div>
            <div>
              <select
                className="fe-select"
                value={prec.emissions.secIndirectSource || ""}
                onChange={(e) => {
                  const src = e.target.value;
                  updatePrecursorField(idx, "emissions.secIndirectSource", src);
                  if (isDefaultSource(src)) {
                    updatePrecursorField(
                      idx,
                      "emissions.secIndirect",
                      getDefaultSEEValue(prec.emissions.cnCode, "secIndirect", reportingYear),
                    );
                  }
                }}
              >
                <option value="">Source...</option>
                {SourceOptionsDirect.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* iii — Electricity emission factor */}
          <div className="fe-em-row">
            <div className="fe-em-label">
              iii · Electricity emission factor (for SEE indirect)
            </div>
            <div>
              <span className="fe-em-unit">{UNITS.TCO2_PER_MWH}</span>
            </div>
            <div>
              <input
                className={`fe-input ${isDefaultSource(prec.emissions.eefIndirectSource) ? "lblue" : ""}`}
                type="number"
                value={prec.emissions.eefIndirect}
                onChange={(e) =>
                  updatePrecursorField(
                    idx,
                    "emissions.eefIndirect",
                    e.target.value,
                  )
                }
              />
            </div>
            <div
  style={{
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  }}
>
  <select
    className="fe-select"
    value={prec.emissions.eefIndirectSource || ""}
    onChange={(e) => {
      const src = e.target.value;
      updatePrecursorField(idx, "emissions.eefIndirectSource", src);

      if (isDefaultSource(src)) {
        updatePrecursorField(
          idx,
          "emissions.eefIndirect",
          getDefaultSEEValue(
            prec.emissions.cnCode,
            "eefIndirect",
            reportingYear
          ),
        );
      }
    }}
  >
    <option value="">Source...</option>

    {SourceOptionsEEF.map((opt) => (
      <option key={opt} value={opt}>
        {opt}
      </option>
    ))}
  </select>

  <span
    style={{
      marginTop: "6px",
      color: "#1976d2",
      fontSize: "12px",
    }}
  >
    {prec.emissions.eefIndirectSource}
  </span>
</div>
</div>

{/* iv — SEE indirect (auto) */}
          {/* iv — SEE indirect (auto) */}
          <div className="fe-em-row">
            <div className="fe-em-label">
              iv · Specific embedded indirect emissions (SEE indirect)
              <span className="hint">
                {indirectIsDefault ? "auto: CN code default" : "auto: ii × iii"}
              </span>
            </div>
            <div>
              <span className="fe-em-unit">{UNITS.TCO2E_PER_T}</span>
            </div>
            <div>
              <input
                className="fe-input lblue"
                readOnly
                value={prec.emissions.seeIndirect || ""}
              />
            </div>
            <div />
          </div>

          {/* v — Total direct emissions (auto) */}
          <div className="fe-em-row">
            <div className="fe-em-label">
              v · Total direct emissions<span className="hint">auto</span>
            </div>
            <div>
              <span className="fe-em-unit">{UNITS.TCO2E}</span>
            </div>
            <div>
              <input
                className="fe-input lblue"
                readOnly
                value={prec.emissions.totalDirectEmissions || ""}
              />
            </div>
            <div />
          </div>

          {/* vi — Total indirect emissions (auto) */}
          <div className="fe-em-row">
            <div className="fe-em-label">
              vi · Total indirect emissions<span className="hint">auto</span>
            </div>
            <div>
              <span className="fe-em-unit">{UNITS.TCO2E}</span>
            </div>
            <div>
              <input
                className="fe-input lblue"
                readOnly
                value={prec.emissions.totalIndirectEmissions || ""}
              />
            </div>
            <div />
          </div>
        </div>

        {/* vii — Justification */}
        <div className="fe-sub-label">
          vii · Justification for use of default values
          {currentCase === "case2" && (
            <span className="hint" style={{ color: "#dc2626" }}>
              {" "}* required (default values used)
            </span>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <select
            className="fe-select"
            value={prec.emissions.justification || ""}
            onChange={(e) => {
              const val = e.target.value;
              updatePrecursorField(idx, "emissions.justification", val);
              if (val !== "Other") {
                updatePrecursorField(idx, "emissions.justificationOther", "");
              }
            }}
          >
            <option value="">Select justification...</option>
            {JustificationOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          {prec.emissions.justification === "Other" && (
            <textarea
              className="fe-textarea"
              placeholder="Please specify the reason..."
              value={prec.emissions.justificationOther || ""}
              onChange={(e) =>
                updatePrecursorField(
                  idx,
                  "emissions.justificationOther",
                  e.target.value,
                )
              }
              maxLength={500}
            />
          )}
        </div>
      </div>
    );
  };

  // ─── Render a single precursor ─────────────────────────────────────────────
  const renderPrecursor = (prec, idx) => {
    //  Control balance recompute (for sub-tab strip + right panel)
    const totalPurchased = Number(prec.totalPurchaseConsumption) || 0;
    const consumedInProc = (prec.consumedInProcesses || []).reduce(
      (s, r) => s + (Number(r.amounts) || 0),
      0,
    );
    const consumedOther = Number(prec.consumedOtherPurposes?.[0]?.amounts) || 0;
    const balance = +(totalPurchased - consumedInProc - consumedOther).toFixed(
      4,
    );
    const balanceOk = Math.abs(balance) < 0.001;

    const seeDirect = Number(prec.emissions.seeDirect) || 0;
    const seeIndirect = Number(prec.emissions.seeIndirect) || 0;
    const seeTotal = seeDirect + seeIndirect;
    const seeHasValue = seeTotal !== 0;

    const linkedProcessLabel = prec.linkedProcessId
      ? processOptions.find((p) => p.id === prec.linkedProcessId)?.label ||
        prec.linkedProcessId
      : null;

    return (
      <div className="fe-grid">
        {/* LEFT: precursor content */}
        <div className="fe-content">
          {/* Precursor meta + title input */}
          <div className="fe-prec-meta">
            <div className="fe-prec-meta-row">
              <div>
                <div className="fe-prec-meta-title-lbl">Precursor title</div>
                <input
                  className="fe-prec-meta-input"
                  value={prec.title || ""}
                  onChange={(e) =>
                    updatePrecursorField(idx, "title", e.target.value)
                  }
                  placeholder="e.g. Pig iron from Supplier X"
                />
              </div>
              <button
                className="fe-btn-delete"
                onClick={() => handleRemovePrecursor(idx)}
                disabled={precursors.length === 1}
                title={
                  precursors.length === 1
                    ? "Cannot delete the only precursor"
                    : "Delete this precursor"
                }
              >
                <DeleteOutlineIcon sx={{ fontSize: 14 }} /> Delete
              </button>
            </div>
            {(prec.countryCode || prec.goodsCategory || linkedProcessLabel) && (
              <div className="fe-prec-chips">
                {prec.countryCode && (
                  <span className="fe-prec-chip fe-prec-chip-country">
                    {prec.countryCode}
                  </span>
                )}
                {prec.goodsCategory && (
                  <span className="fe-prec-chip">{prec.goodsCategory}</span>
                )}
                {linkedProcessLabel && (
                  <span className="fe-prec-chip fe-prec-chip-process">
                    → {linkedProcessLabel}
                  </span>
                )}
                {prec.originRoutes?.length > 0 &&
                  prec.originRoutes.map((r) => (
                    <span key={r} className="fe-prec-chip">
                      {r}
                    </span>
                  ))}
              </div>
            )}
          </div>

          {/* Sub-tabs */}
          <div className="fe-subtabs">
            <div
              className={`fe-subtab ${getSubTab(idx) === "balance" ? "active" : ""}`}
              onClick={() => setSubTabFor(idx, "balance")}
            >
              <span className="fe-subtab-num">01</span>
              <span>Material Balance</span>
              <span className="fe-subtab-meta">
                {fmt(prec.totalPurchaseConsumption)} t · {balance}{" "}
                {balanceOk ? "✓" : "✗"}
              </span>
            </div>
            <div
              className={`fe-subtab ${getSubTab(idx) === "emissions" ? "active" : ""}`}
              onClick={() => setSubTabFor(idx, "emissions")}
            >
              <span className="fe-subtab-num">02</span>
              <span>Specific Embedded Emissions</span>
              <span className="fe-subtab-meta">
                SEE: {fmt(seeTotal)} tCO₂e/t
              </span>
            </div>
          </div>

          {/* Active sub-tab */}
          {getSubTab(idx) === "balance" && renderMaterialBalance(prec, idx)}
          {getSubTab(idx) === "emissions" && renderSEE(prec, idx)}
        </div>

        {/* RIGHT: sticky SEE panel */}
        <div className="fe-sticky-col">
          <div className="fe-sticky">
            <div className="fe-see-card">
              <div className="fe-see-card-title">
                Precursor {idx + 1} · {getPrecursorProgress(prec).progress}%
                complete
              </div>

              <div className="fe-see-item">
                <span className="k">SEE direct</span>
                <span>
                  <span className={`v ${!seeHasValue ? "zero" : ""}`}>
                    {fmt(prec.emissions.seeDirect)}
                  </span>
                  <span className="unit">tCO₂e/t</span>
                </span>
              </div>
              <div className="fe-see-item">
                <span className="k">SEE indirect</span>
                <span>
                  <span className={`v ${!seeHasValue ? "zero" : ""}`}>
                    {fmt(prec.emissions.seeIndirect)}
                  </span>
                  <span className="unit">tCO₂e/t</span>
                </span>
              </div>
              <div className="fe-see-item">
                <span className="k">Total direct em.</span>
                <span>
                  <span className={`v ${!seeHasValue ? "zero" : ""}`}>
                    {fmt(prec.emissions.totalDirectEmissions)}
                  </span>
                  <span className="unit">tCO₂e</span>
                </span>
              </div>
              <div className="fe-see-item">
                <span className="k">Total indirect em.</span>
                <span>
                  <span className={`v ${!seeHasValue ? "zero" : ""}`}>
                    {fmt(prec.emissions.totalIndirectEmissions)}
                  </span>
                  <span className="unit">tCO₂e</span>
                </span>
              </div>

              <div className="fe-see-grand">
                <div className="k">SEE total · direct + indirect</div>
                <div>
                  <span className={`v ${!seeHasValue ? "zero" : ""}`}>
                    {fmt(seeTotal)}
                  </span>
                  <span className="unit">tCO₂e/t</span>
                </div>
              </div>
            </div>

            {/* Small precursor meta card */}
            <div className="fe-prec-meta" style={{ margin: 0 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "8px 14px",
                }}
              >
                <div>
                  <div className="fe-prec-meta-title-lbl">Goods</div>
                  <div
                    style={{ fontSize: 12, color: "#1e293b", fontWeight: 600 }}
                  >
                    {prec.goodsCategory || "—"}
                  </div>
                </div>
                <div>
                  <div className="fe-prec-meta-title-lbl">Country</div>
                  <div
                    style={{ fontSize: 12, color: "#1e293b", fontWeight: 600 }}
                  >
                    {prec.countryCode || "—"}
                  </div>
                </div>
                <div>
                  <div className="fe-prec-meta-title-lbl">Linked process</div>
                  <div
                    style={{ fontSize: 11, color: "#1e293b", fontWeight: 600 }}
                  >
                    {linkedProcessLabel || "—"}
                  </div>
                </div>
                <div>
                  <div className="fe-prec-meta-title-lbl">Balance</div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: balanceOk ? "#15803d" : "#b91c1c",
                    }}
                  >
                    {balance} {balanceOk ? "✓" : "✗"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const activeProc = precursors[activeIdx];
  const isLastPrec = activeIdx === precursors.length - 1;

  return (
    <>
      <style>{STYLES}</style>

      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f8fafc" }}>
        <AuditSidebar displayAuditId={auditId} />

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div className="fe">
            {/* Slim header */}
            <div className="fe-header-slim">
              <div className="title">Purchased Precursors</div>
            </div>

            <div className="fe-body">
              {/* Sector chip (FYI only) */}
              {installSector && (
                <div className="fe-sector-bar">
                  <span>Sector:</span>
                  <span className="fe-sector-chip">{installSector}</span>
                  <Tooltip
                    title="Indirect emissions are priced under CBAM for Cement + Fertilisers (Annex II). All sectors may still report indirect data for any precursor."
                    arrow
                  >
                    <span className="fe-sector-note">
                      indirect:{" "}
                      {sectorPricesIndirect ? "priced" : "report-only"}
                    </span>
                  </Tooltip>
                </div>
              )}

              {/* Precursor tabs + "+" add tab */}
              <div className="fe-tabs">
                {precursors.map((p, i) => (
                  <div
                    key={p.id || i}
                    className={`fe-tab ${i === activeIdx ? "active" : ""}`}
                    onClick={() => setActiveIdx(i)}
                  >
                    <span className="fe-tab-num">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="fe-tab-title">
                      {p.title?.trim() || `Precursor ${i + 1}`}
                    </span>
                    {p.countryCode && (
                      <span className="fe-tab-chip">{p.countryCode}</span>
                    )}
                    <span
                      className={`fe-tab-badge ${tabBadgeClass(getPrecursorProgress(p).progress)}`}
                    >
                      {getPrecursorProgress(p).progress}%
                    </span>
                  </div>
                ))}
                <button
                  className="fe-tab-add"
                  onClick={handleAddPrecursor}
                  title="Add a new precursor"
                >
                  <AddIcon sx={{ fontSize: 14 }} /> Add
                </button>
              </div>

              {/* Active precursor */}
              {activeProc && renderPrecursor(activeProc, activeIdx)}

              {/* Footer */}
              <div className="fe-footer-bar">
                <button
                  className="fe-btn-prev"
                  onClick={handleBack}
                  disabled={isSaving}
                >
                  ← Back
                </button>
                {getSubTab(activeIdx) === "balance" ? (
                  <button
                    className="fe-btn-save"
                    onClick={() => {
                      setSubTabFor(activeIdx, "emissions");
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    disabled={isSaving}
                  >
                    Next →
                  </button>
                ) : isLastPrec ? (
                  <button
                    className="fe-btn-save"
                    onClick={handleSaveAndNext}
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Save & Continue →"}
                  </button>
                ) : (
                  <button
                    className="fe-btn-save"
                    onClick={handleNextPrecursor}
                    disabled={isSaving}
                  >
                    {isSaving
                      ? "Saving..."
                      : `Next precursor (${String(activeIdx + 2).padStart(2, "0")}) →`}
                  </button>
                )}
              </div>
            </div>

            {toast && (
              <div
                className={`fe-toast ${toast.type === "error" ? "error" : ""}`}
              >
                {toast.type === "error" ? "×" : "✓"} {toast.msg}
              </div>
            )}

            {/* Delete confirmation modal — preserved from original */}
            <Dialog
              open={deleteConfirm !== null}
              onClose={() => setDeleteConfirm(null)}
            >
              <DialogTitle>Delete precursor?</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure you want to delete{" "}
                  <strong>
                    {deleteConfirm !== null &&
                      (precursors[deleteConfirm]?.title?.trim() ||
                        `Precursor ${deleteConfirm + 1}`)}
                  </strong>
                  ? This cannot be undone. Any data linked from Form D will need
                  to be re-entered.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                <Button
                  onClick={confirmDelete}
                  color="error"
                  variant="contained"
                >
                  Yes, delete
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        </Box>
      </Box>
    </>
  );
};

export default FormE;